// Transport section — logistics employee row + per-leg row cards + per-currency
// total summary footer. Logistics employee owns transport setup, so the
// inline-editable assignee sits at the top of the section.
import React from "react";
import { useTranslation } from "react-i18next";
import { TruckOutlined, InboxOutlined } from "@ant-design/icons";
import type { Transaction, Transport } from "@shared/types/transaction";
import { AmountDisplay } from "@ds/display/AmountDisplay";
import { Pill } from "@ds/display/Pill";
import { EmptyState } from "@ds/display/EmptyState";
import { EntityLink } from "@ds/connected/EntityLink";
import { Link } from "@ds/primitives/Link";
import { InvoicedStatusTag } from "@ds/domain/EnumTag";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { Grid } from "@ds/layout/Grid";
import { TextValue } from "@ds/display/TextValue";
import {
  type ToneName,
} from "@ds/tokens/palette";
import { sumByCurrency } from "@shared/calc";
import { useSubQuery } from "@ds/data/subQueriesContext";

const TYPE_TONE: Record<string, ToneName> = {
  ship: "blue", train: "purple", spedition: "amber", truck: "teal",
};

// Inline transport-mode glyphs (the shared @ds Icon library has truck only).
const ShipSvg: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 20a4 4 0 0 0 3.5 2 4 4 0 0 0 3-2 4 4 0 0 1 3-2 4 4 0 0 1 3 2 4 4 0 0 0 3 2 4 4 0 0 0 3.5-2" />
    <path d="M20 17.5 22 12H2l2 5.5" /><path d="M5 12V7h14v5" /><path d="M12 7V3" />
  </svg>
);
const TrainSvg: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="3" width="16" height="16" rx="2" /><path d="M4 11h16" />
    <path d="M8 19l-2 3" /><path d="M16 19l2 3" /><circle cx="9" cy="15" r="1" /><circle cx="15" cy="15" r="1" />
  </svg>
);

function TypeIcon({ kind }: { kind: Transport["transport_type"] }) {
  if (kind === "ship")      return <ShipSvg />;
  if (kind === "train")     return <TrainSvg />;
  if (kind === "spedition") return <InboxOutlined />;
  return <TruckOutlined />;
}

function typeLabel(t: (k: string, fb: string) => string, kind: Transport["transport_type"]): string {
  return t(`transactions.transport.type.${kind ?? "truck"}`,
    kind === "ship" ? "Statek" : kind === "train" ? "Pociąg"
    : kind === "spedition" ? "Spedycja" : "Ciężarówka");
}

export interface TransactionTransportProps {
  transaction: Transaction;
}

// ─── Per-leg row card ────────────────────────────────────────────────────────

const LegRow: React.FC<{ leg: Transport; t: (k: string, fb: string) => string }> = ({ leg, t }) => {
  const tone   = TYPE_TONE[leg.transport_type ?? "truck"] ?? "grey";
  const vehicleVal = [leg.vehicle_plate, leg.container_number].filter(Boolean).join(" · ");

  return (
    <Box size="md" border="strong" shape="rounded-lg" tone="neutral">
      <Grid columns={3} gap="md" align="center">
        {/* ── Left: sequence pill + type icon badge ── */}
        <Inline as="span" inline gap="sm" align="center">
          <Pill tone={tone} size="sm">#{leg.sequence_number}</Pill>
          <Box size="xs" tone={tone} shape="rounded">
            <TypeIcon kind={leg.transport_type} />
          </Box>
        </Inline>

        {/* ── Middle: carrier + meta lines ── */}
        <Stack gap="xs">
          <Inline as="span" inline gap="sm" align="center" wrap>
            {leg.transport_company_id != null && leg.company?.short_name
              ? <EntityLink type="company" id={leg.transport_company_id} label={leg.company.short_name} variant="inline" />
              : <TextValue tone="faint" italic>{t("transactions.transport.no_carrier", "Brak przewoźnika")}</TextValue>}
            <TextValue size="xs" tone="muted" uppercase weight="semibold">
              {typeLabel(t, leg.transport_type)}
            </TextValue>
          </Inline>
          <Inline as="span" inline gap="md" align="baseline" wrap>
            {leg.transit && (
              <TextValue as="span" size="sm" tone="secondary">
                <TextValue as="span" tone="muted">{t("transactions.transport.route", "Trasa")}: </TextValue>{leg.transit}
              </TextValue>
            )}
            {leg.driver_name && (
              <TextValue as="span" size="sm" tone="secondary">
                <TextValue as="span" tone="muted">{t("transactions.transport.driver", "Kierowca")}: </TextValue>
                {leg.driver_name}
                {leg.driver_phone && (
                  <>
                    {" · "}
                    <Link kind="tel" href={`tel:${leg.driver_phone}`} tone="secondary" />
                  </>
                )}
              </TextValue>
            )}
            {vehicleVal && (
              <TextValue as="span" size="sm" tone="secondary">
                <TextValue as="span" tone="muted">
                  {leg.transport_type === "ship" || leg.transport_type === "train"
                    ? t("transactions.transport.container", "Kontener")
                    : t("transactions.transport.vehicle",   "Pojazd")}
                  {": "}
                </TextValue>
                <TextValue as="span" mono tabular>{vehicleVal}</TextValue>
              </TextValue>
            )}
            {/* Payer — always-set column (a DB trigger defaults it to the
                carrier), so only surface it when it actually diverges (a
                spedition billing on the carrier's behalf). */}
            {leg.commercial_company_id != null && leg.commercial_company_id !== leg.transport_company_id && (
              <TextValue as="span" size="sm" tone="secondary">
                <TextValue as="span" tone="muted">{t("transactions.transport.payer", "Płatnik")}: </TextValue>
                <EntityLink
                  type="company"
                  id={leg.commercial_company_id}
                  label={leg.commercial_company?.short_name ?? `#${leg.commercial_company_id}`}
                  variant="inline"
                />
              </TextValue>
            )}
          </Inline>
        </Stack>

        {/* ── Right: price + status pills ── */}
        <Stack gap="xs" align="end">
          {leg.price != null
            ? <AmountDisplay
                value={leg.price} currency={leg.price_currency}
                colored={leg.price < 0} strong
                suffix={leg.price_type === "per_ton" ? "/t" : undefined}
              />
            : <TextValue tone="faint">—</TextValue>}
          <Inline as="span" inline gap="xs" wrap justify="end">
            <InvoicedStatusTag value={leg.invoiced_status ?? null} />
          </Inline>
        </Stack>
      </Grid>
    </Box>
  );
};

// ─── Section ─────────────────────────────────────────────────────────────────

export const TransactionTransport: React.FC<TransactionTransportProps> = ({ transaction: tx }) => {
  const { t } = useTranslation();
  const { rows: legs } = useSubQuery<Transport>("transports", tx.id);

  const tt = (k: string, fb: string) => t(k, fb);

  if (!legs.length) {
    return (
      <EmptyState title={t("transactions.empty.transport", "Brak zdefiniowanego transportu")} size="sm" />
    );
  }

  // Per-currency totals (legs include `per_ton` legs as |price| absolute values).
  const totals = sumByCurrency(legs.map((l) => {
    const w = tx.unloaded_weight ?? tx.loaded_weight ?? 0;
    const v = l.price_type === "per_ton" ? (l.price ?? 0) * w : (l.price ?? 0);
    return { price: v, price_currency: l.price_currency };
  }));
  const totalEntries = Object.entries(totals);

  return (
    <>
      <Stack gap="sm">
        {legs.map((leg) => <LegRow key={leg.id} leg={leg} t={tt} />)}

        {/* Footer — per-currency total summary. */}
        {totalEntries.length > 0 && (
          <Box size="sm" shape="rounded" tone="muted">
            <Inline align="center" justify="between">
              <TextValue size="xs" tone="muted" weight="semibold" uppercase>
                {t("transactions.transport.total", "Razem koszt transportu")}
              </TextValue>
              <Inline as="span" inline gap="md" align="baseline">
                {totalEntries.map(([ccy, sum]) => (
                  <AmountDisplay key={ccy} value={-sum} currency={ccy} colored strong />
                ))}
              </Inline>
            </Inline>
          </Box>
        )}
      </Stack>
    </>
  );
};
